// Global Instructions Rule Applied!
//
// The select lists of the Commitment forms (view__commitment_form_options):
// commitment statuses, additional notice documents, the standalone funds and
// consolidated funds, and — for the New commitment form — the investor
// entities of one account. Classifications and SBIC investor types come from
// the global configs.
import { useCallback, useEffect, useMemo, useState } from 'react'

import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import type { CommitmentFormOptions } from '../../../state_manager/data_models/commitment_form.ts'
import type { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import { fetchCommitmentFormOptions } from '../services/commitmentsService.ts'

const EMPTY: SelectListEntry[] = []

export function useCommitmentFormOptions(accountId: string | null = null) {
  const [options, setOptions] = useState<CommitmentFormOptions | null>(null)
  const [loading, setLoading] = useState(true)
  // which account `options.investors` belongs to: a form that switches account
  // must not offer the previous account's investors while the next list loads
  const [loadedAccountId, setLoadedAccountId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const res = await fetchCommitmentFormOptions(accountId, getAccessToken(store.getState().auth))
    if (res.error) showErrorToast(res.error)
    return res.data
  }, [accountId])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const data = await load()
      if (cancelled) return
      setOptions(data)
      setLoadedAccountId(accountId)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [load, accountId])

  const statusOptions = useMemo<SelectListEntry[]>(() => options?.statuses ?? EMPTY, [options])
  const noticeSupplementOptions = useMemo<SelectListEntry[]>(() => options?.notice_supplements ?? EMPTY, [options])
  const investors = useMemo(
    () => (loadedAccountId === accountId ? (options?.investors ?? []) : []),
    [options, loadedAccountId, accountId]
  )

  return {
    options,
    loading,
    /** true once the investors list is the one of `accountId` */
    investorsReady: !loading && loadedAccountId === accountId,
    investors,
    statusOptions,
    noticeSupplementOptions
  }
}

export default useCommitmentFormOptions
